import { useContext } from '@berlin-front/common/composables/context';
import { BerlinBffContext } from '@services/BerlinBff';
import { readonly, reactive, unref, watch, ref, computed } from '@vue/composition-api';
import { useMutation, useQuery } from 'vue-query';
import { AtoneReauthorizePutResponseAuthorizationResultEnum } from '@netprotections/berlin-bff';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import { usePrepaymentCheck } from '@services/service-type-bridge/atone-common';
import { event } from 'vue-gtag';
import { SERVICE_TYPE } from '@/services/ModalParameterContext';
import { ApiCacheContext } from '@/services/ApiCacheContext';
import { getRefDeep } from '@berlin-front/common/utils';
import { broadcast } from '@/broadcast';
import { ROUTE_NAME } from '@/router/route-name';
import useErrors from '@composables/useErrors';
import useIsLegacy from '@composables/useIsLegacy';

const REAUTHORIZE_TIMEOUT = 20000;

export const useLoading = () => ({
  useLoading: () => {
    const [isLegacy] = useIsLegacy();
    const router = useRouter();
    const apiCacheContext = useContext(ApiCacheContext);
    const { payment, js, point } = useContext(BerlinBffContext);
    const {
      atoneReauthorizePutResult,
      setAtoneReauthorizePutResult,
      tokens,
      setAtonePointGetResult,
      atoneModalParameter,
    } = useContext(ApiCacheContext);
    const prePaymentCheck = usePrepaymentCheck();

    const completeProgress = ref(false);
    const nextPagePath = ref('');
    const requireJsPayment = ref(true);
    const npToken = computed(() => unref(apiCacheContext.tokens).npToken);
    const transactionId = apiCacheContext.atoneCapturePostResult.value?.id;
    const substractPoint = apiCacheContext.atoneCapturePostResult.value?.subtractPoint;

    // TODO: prepaymentチェック。
    const {
      mutate: check,
      isSuccess: isSuccessPrepaymentCheck,
      data: prePaymentCheckResult,
    } = useMutation('prepaymentCheck', async () => prePaymentCheck());
    check();

    const {
      isSuccess: isSuccessJsPayment,
      isError: isErrorJsPayment,
      mutate: mutateJsPaymentCapture,
    } = useMutation(
      'atone.JsPaymentCapturePost',
      () =>
        js.atoneJavascriptControllerJavascriptPaymentCapture({
          id: getRefDeep(atoneReauthorizePutResult, 'id'),
        }),
      {},
    );

    // APIの呼び出し(submit)
    const { isSuccess: isSuccessPayment, mutate } = useMutation(
      'atone.PaymentReauthorizePut',
      () =>
        payment.atonePaymentControllerReauthorizePut(
          transactionId || '',
          unref(npToken) || '',
          atoneModalParameter.value.pubKey,
          {
            subtractPoint: substractPoint || 0,
            trackToken: getRefDeep(apiCacheContext.tokens, 'trustedTrackToken'),
            userNo: atoneModalParameter.value.payment.userNo,
            salesSettled: atoneModalParameter.value.payment.salesSettled,
            shopTransactionNo: atoneModalParameter.value.payment.shopTransactionNo,
            isOldParameter: isLegacy.value,
          },
        ),
      {
        retry: false,
        onSuccess: ({ data }) => {
          setAtoneReauthorizePutResult(data);
          let newrelicResult: 'payment_complete' | 'payment_ng' = 'payment_complete';
          const { authorizationResult } = data;
          if (authorizationResult === AtoneReauthorizePutResponseAuthorizationResultEnum.NUMBER_1) {
            // 決済成功。
            mutateJsPaymentCapture();
          } else {
            // 決済失敗。
            newrelicResult = 'payment_ng';
            requireJsPayment.value = false;
            // TODO: ROUTE_NAMEを使用するよう修正
            nextPagePath.value = '/atone/fail/auth-ng';
          }

          // newrelic(決済成功/失敗)
          window.newrelic.addPageAction(newrelicResult, {
            revenue: atoneModalParameter.value.payment.amount,
            coupon: substractPoint || 0,
            products: atoneModalParameter.value.payment.items.map((item) => ({
              id: item.shopItemId,
              price: item.itemPrice,
              name: item.itemName,
              quantity: item.itemCount,
            })),
            shop_transaction_no: atoneModalParameter.value.payment.shopTransactionNo,
          });

          event('purchase', {
            transaction_id: atoneModalParameter.value.payment.shopTransactionNo,
            value: atoneModalParameter.value.payment.amount,
            coupon: substractPoint || 0,
            currency: 'JPY',
            items: atoneModalParameter.value.payment.items.map((item) => ({
              id: item.shopItemId,
              price: item.itemPrice,
              name: item.itemName,
              quantity: item.itemCount,
            })),
          });

          event('reauthorize', {
            reauthorize: 'yes',
          });
        },
      },
    );

    watch([isSuccessPrepaymentCheck, prePaymentCheckResult] as const, ([isSuccess, result]) => {
      if (isSuccess) {
        switch (result) {
          case true: {
            mutate();
            break;
          }
          case false: {
            // TODO: lite用のカート不整合ルート作る。
            void router.replace({ name: ROUTE_NAME.ATONE_FAIL_CART });
            break;
          }
          default: {
            ((_v: undefined) => {})(result);
          }
        }
      }
    });

    watch([completeProgress, nextPagePath], () => {
      const complete = unref(completeProgress);
      const path = unref(nextPagePath);
      if (complete) {
        if (
          unref(atoneReauthorizePutResult)?.authorizationResult ===
          AtoneReauthorizePutResponseAuthorizationResultEnum.NUMBER_1
        ) {
          broadcast.send({
            type: 'MODAL_CONTROL_SUCCEEDED',
            payload: { serviceType: SERVICE_TYPE.ATONE },
          });
        }
        if (path) {
          void router.replace({ path });
        }
      }
    });

    // 処理の終了。
    const isCompleted = computed(() => {
      if (unref(requireJsPayment)) {
        return unref(isSuccessPayment) && (unref(isSuccessJsPayment) || unref(isErrorJsPayment));
      }
      return unref(isSuccessPayment);
    });
    const isLoading = computed(() => !unref(isCompleted));

    // エラー処理
    // エラー時のポイント取得
    const { refetch } = useQuery(
      'atone.atonePointGet',
      () =>
        point.atonePointControllerPointGet(
          getRefDeep(tokens, 'npToken'),
          atoneModalParameter.value.pubKey,
          getRefDeep(tokens, 'trustedTrackToken'),
        ),
      {
        enabled: false,
        onSuccess: ({ data }) => {
          setAtonePointGetResult(data);
        },
      },
    );
    const { errors } = useErrors({
      fieldMapper: {},
      defaultFieldName: 'errors',
    });
    watch(errors, async () => {
      await refetch.value();
      void router.replace({
        name: ROUTE_NAME.ATONE_SETTLEMENT_CONFIRM,
        query: {
          alert: errors.value.errors[0],
        },
      });
    });

    return readonly(
      reactive({
        title: '再審査',
        onComplete: () => {
          completeProgress.value = true;
        },
        isLoading,
        isCompleted,
        duration: REAUTHORIZE_TIMEOUT,
      }),
    );
  },
});
